/**
 * Analyze tab element renderers.
 *
 * Viewport visuals for the element categories produced by Analyze tools:
 * - 'Structural Loads'     → BIM-style red/orange load arrows
 * - 'Spaces'               → translucent blue dashed volume + green tag
 * - 'Zones'                → dashed green outline around member spaces
 * - 'Space Separators'     → dashed divider with endpoint nodes
 *
 * Engineering display colors are fixed (not theme-driven), mirroring how
 * BIM standardizes analytical/load presentation colors.
 */

import { useMemo } from 'react';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import { useBIMStore } from '../state/useBIMStore';
import { useAnalysisStore } from '../state/useAnalysisStore';

// ==================== Structural Loads ====================

export function StructuralLoadRenderer({ element, isSelected }: { element: any, isSelected: boolean }) {
    const show = useAnalysisStore((s) => s.showLoads);
    const g = element.geometryData;
    if (!show || !g) return null;

    const color = isSelected ? '#ff3b30' : '#d23f31';

    if (g.loadType === 'Point') {
        const p = g.position as [number, number, number];
        const mag = Number(g.magnitude ?? 0);
        const len = arrowLength(Math.abs(mag), 1, 4);
        return (
            <group position={p} userData={{ id: element.id }}>
                <ArrowGlyph length={len} color={color} />
                <Text position={[0, 0, len + 0.9]} fontSize={0.3} color={color} anchorX="center">
                    {formatLoad(mag, 'N')}
                </Text>
            </group>
        );
    }

    if (g.loadType === 'Line' && Array.isArray(g.start) && Array.isArray(g.end)) {
        const start = new THREE.Vector3(...(g.start as [number, number, number]));
        const end = new THREE.Vector3(...(g.end as [number, number, number]));
        const length = start.distanceTo(end);
        const count = Math.max(3, Math.min(9, Math.round(length / 0.8)));
        const mag = Number(g.magnitude ?? 0);
        const len = arrowLength(Math.abs(mag), 0.6, 2.5);
        const arrows: THREE.Vector3[] = [];
        for (let i = 0; i <= count; i++) {
            arrows.push(start.clone().lerp(end, i / count));
        }
        return (
            <group userData={{ id: element.id }}>
                {arrows.map((a, i) => (
                    <group key={i} position={a}>
                        <ArrowGlyph length={len} color={color} />
                    </group>
                ))}
                {arrows.length > 1 && (
                    <Line points={arrows.map(a => a.clone().add(new THREE.Vector3(0, 0, len)))} color={color} lineWidth={2} />
                )}
                <Text
                    position={start.clone().lerp(end, 0.5).add(new THREE.Vector3(0, 0, len + 0.8))}
                    fontSize={0.3}
                    color={color}
                    anchorX="center"
                >
                    {formatLoad(mag, 'N/m')}
                </Text>
            </group>
        );
    }

    if (g.loadType === 'Area' && Array.isArray(g.boundary)) {
        const loop = (g.boundary as [number, number, number][]).map(p => new THREE.Vector3(...p));
        const mag = Number(g.magnitude ?? 0);
        const len = arrowLength(Math.abs(mag), 0.8, 3);
        const centroid = loop.length > 0
            ? loop.reduce((acc, p) => acc.add(p), new THREE.Vector3()).divideScalar(loop.length)
            : new THREE.Vector3();
        return (
            <group userData={{ id: element.id }}>
                {loop.length > 2 && (
                    <mesh>
                        <shapeGeometry args={[shapeFrom(loop)]} />
                        <meshBasicMaterial color="#e2622e" transparent opacity={0.12} side={THREE.DoubleSide} />
                    </mesh>
                )}
                <Line points={[...loop, loop[0]]} color="#e2622e" lineWidth={2} dashed dashSize={0.3} gapSize={0.15} />
                {sampleArrows(loop, centroid, Math.max(4, loop.length * 2)).map((p, i) => (
                    <group key={i} position={p}>
                        <ArrowGlyph length={len} color="#e2622e" />
                    </group>
                ))}
                <Text position={centroid.clone().add(new THREE.Vector3(0, 0, len + 0.8))} fontSize={0.32} color="#e2622e" anchorX="center">
                    {formatLoad(mag, 'N/m²')}
                </Text>
            </group>
        );
    }

    return null;
}

/** Red downward arrow: shaft + head, pointing from +Z toward the node. */
function ArrowGlyph({ length, color }: { length: number; color: string }) {
    const head = Math.min(0.35, length * 0.3);
    return (
        <group>
            <Line
                points={[
                    [0, 0, length],
                    [0, 0, head],
                ]}
                color={color}
                lineWidth={2}
            />
            <mesh position={[0, 0, head / 2]} rotation={[Math.PI, 0, 0]}>
                <coneGeometry args={[0.09, head, 10]} />
                <meshBasicMaterial color={color} />
            </mesh>
        </group>
    );
}

/** Magnitude-based arrow length clamped to a readable range. */
function arrowLength(magN: number, min: number, max: number): number {
    const m = magN / 1000; // kN
    return Math.min(max, Math.max(min, 0.6 + m * 0.05));
}

function formatLoad(mag: number, unit: string): string {
    const v = Math.abs(mag);
    if (v >= 1000) return `${(mag / 1000).toFixed(1)} k${unit}`;
    return `${mag.toFixed(0)} ${unit}`;
}

/** Grid-sampled interior points for area load arrows. */
function sampleArrows(loop: THREE.Vector3[], centroid: THREE.Vector3, count: number): THREE.Vector3[] {
    const pts: THREE.Vector3[] = [centroid.clone()];
    const n = loop.length;
    for (let i = 0; i < n && pts.length < count; i++) {
        pts.push(loop[i].clone().lerp(centroid, 0.5));
    }
    for (let i = 0; i < n && pts.length < count; i++) {
        pts.push(loop[i].clone().lerp(loop[(i + 1) % n], 0.5));
    }
    return pts.slice(0, count);
}

function shapeFrom(points: THREE.Vector3[]): THREE.Shape {
    const shape = new THREE.Shape();
    points.forEach((p, i) => (i === 0 ? shape.moveTo(p.x, p.y) : shape.lineTo(p.x, p.y)));
    shape.closePath();
    return shape;
}

// ==================== Spaces ====================

export function SpaceRenderer({ element, isSelected }: { element: any, isSelected: boolean }) {
    const show = useAnalysisStore((s) => s.showSpaces);
    const g = element.geometryData;
    const height = Number(element.parameters?.['Height'] ?? 3);

    const box = useMemo<THREE.Vector2[] | null>(() => {
        if (!g || !Array.isArray(g.boundary)) return null;
        const loop = (g.boundary as [number, number][]).map(p => new THREE.Vector2(p[0], p[1]));
        return loop;
    }, [g]);

    if (!show || !box || box.length < 3) return null;
    const levelZ = Number(g.levelZ ?? 0);
    const top = levelZ + Math.max(0.5, height);

    // Top, bottom and vertical edge loop of the dashed volume.
    const bottom = box.map(p => new THREE.Vector3(p.x, p.y, levelZ));
    const topPts = box.map(p => new THREE.Vector3(p.x, p.y, top));
    const centroid = bottom.reduce((a, p) => a.add(p), new THREE.Vector3()).divideScalar(bottom.length);

    return (
        <group userData={{ id: element.id }}>
            <Line points={[...bottom, bottom[0]]} color="#2f6fb8" lineWidth={1.6} dashed dashSize={0.25} gapSize={0.15} />
            <Line points={[...topPts, topPts[0]]} color="#2f6fb8" lineWidth={1.6} dashed dashSize={0.25} gapSize={0.15} />
            {bottom.map((p, i) => (
                <Line key={i} points={[p, topPts[i]]} color="#2f6fb8" lineWidth={1.2} dashed dashSize={0.2} gapSize={0.15} />
            ))}
            <mesh position={[centroid.x, centroid.y, (levelZ + top) / 2]}>
                <boxGeometry args={[0.001, 0.001, 0.001]} />
                <meshBasicMaterial visible={false} />
            </mesh>
            <TagBubble
                position={[centroid.x, centroid.y, top + 0.25]}
                name={String(element.parameters?.['Name'] ?? element.name ?? 'Space')}
                highlight={isSelected}
            />
        </group>
    );
}

function TagBubble({ position, name, highlight }: { position: [number, number, number]; name: string; highlight: boolean }) {
    return (
        <group position={position}>
            <mesh>
                <planeGeometry args={[Math.max(1, name.length * 0.28), 0.55]} />
                <meshBasicMaterial color={highlight ? '#3fae4a' : '#4c9a3f'} transparent opacity={0.92} side={THREE.DoubleSide} />
            </mesh>
            <Text position={[0, 0, 0.01]} fontSize={0.26} color="#ffffff" anchorX="center" anchorY="middle" maxWidth={name.length * 0.3}>
                {name}
            </Text>
        </group>
    );
}

// ==================== Zones ====================

export function ZoneRenderer({ element, isSelected }: { element: any, isSelected: boolean }) {
    const show = useAnalysisStore((s) => s.showSpaces);
    const elementsMap = useBIMStore((s) => s.project.elements);
    const ids: string[] = element.geometryData?.spaceIds ?? [];

    const outlines = useMemo(() => {
        const out: { id: string; pts: THREE.Vector3[] }[] = [];
        ids.forEach(id => {
            const sp = elementsMap.get(id);
            const g = (sp as any)?.geometryData;
            if (!g || !Array.isArray(g.boundary)) return;
            const z = Number(g.levelZ ?? 0) + 0.05;
            out.push({
                id,
                pts: g.boundary.map((p: any) => new THREE.Vector3(p[0], p[1], z)),
            });
        });
        return out;
    }, [ids.join(','), elementsMap]);

    if (!show || outlines.length === 0) return null;

    return (
        <group userData={{ id: element.id }}>
            {outlines.map(o => (
                <Line
                    key={o.id}
                    points={[...o.pts, o.pts[0]]}
                    color={isSelected ? '#3fae4a' : '#2e9e44'}
                    lineWidth={2.5}
                    dashed
                    dashSize={0.45}
                    gapSize={0.22}
                />
            ))}
            <Text
                position={outlines[0].pts.reduce((a, p) => a.add(p), new THREE.Vector3()).divideScalar(outlines[0].pts.length).add(new THREE.Vector3(0, 0, 0.6))}
                fontSize={0.34}
                color="#2e7d32"
                anchorX="center"
                outlineWidth={0.02}
                outlineColor="#ffffff"
            >
                {element.name || 'Zone'}
            </Text>
        </group>
    );
}

// ==================== Space Separators ====================

export function SpaceSeparatorRenderer({ element, isSelected }: { element: any, isSelected: boolean }) {
    const show = useAnalysisStore((s) => s.showSpaces);
    const g = element.geometryData;
    if (!show || !g || !Array.isArray(g.start) || !Array.isArray(g.end)) return null;
    const start = new THREE.Vector3(...(g.start as [number, number, number]));
    const end = new THREE.Vector3(...(g.end as [number, number, number]));
    return (
        <group userData={{ id: element.id }}>
            <Line
                points={[start, end]}
                color={isSelected ? '#3fae4a' : '#2e9e44'}
                lineWidth={2.2}
                dashed
                dashSize={0.3}
                gapSize={0.18}
            />
            {[start, end].map((p, i) => (
                <mesh key={i} position={p}>
                    <sphereGeometry args={[0.09, 10, 10]} />
                    <meshBasicMaterial color="#2e9e44" />
                </mesh>
            ))}
        </group>
    );
}
